import { useCallback } from 'react';
import { useAuthStore } from '../../store/authStore';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { setCollections, useCollectionsStore } from '../../store/collectionsStore';
import { setRequestTabSyncRevision, setRequestTabs } from '../../store/tabsStore';
import { collectionService } from '../../services/collectionService';
import { apiRequestService } from '../../services/apiRequestService';
import { findSidebarRequest, findParentCollectionId } from '../../utils/requestResolve';
import { markLocalOnly } from '../../services/localCollectionLink';
import { getFolderById, moveRequestToFolder } from '../../utils/collectionFolders';
import { applyFolderBatch } from '../../services/ai/batch/folderBatch';
import { normalizeSingleBraceVariables, generateLocalEntityId, dedupeCollectionRequestIds } from '../../utils/localIds';
import { resolvedRequestName } from '../../utils/deriveRequestNameFromUrl';
import { defaultRequestAuth } from '../../utils/requestAuth';
import { prepareRequestBodyForApi } from '../../utils/requestPayload';
import { mapApiRequestToSidebar } from '../../utils/requestMappers';
import { isBackendWorkspace } from '../../utils/workspaceStorage';
import { readOnlyWorkspaceRefusal } from '../../plans/capRefusal';
import { apiCapRefusal } from '../../plans/localApiUsage';
import { describeRequestFieldValue } from './aiRequestHelpers';

/** Assistant tools that write collections: create collection / folders / request, move, update. */
export function useAiCollectionWrites({
  scheduleTreeSave,
}) {
  const aiCreateCollection = useCallback(async (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return { error: 'Name is required' };
    const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
    const isAuthenticated = useAuthStore.getState().isAuthenticated;
    const readOnly = readOnlyWorkspaceRefusal(currentWorkspace);
    if (readOnly) return { error: readOnly };

    try {
      if (isBackendWorkspace(currentWorkspace, isAuthenticated)) {
        const newCollection = await collectionService.createCollection({
          name: trimmed,
          workspace_id: currentWorkspace.id,
        });
        setCollections((prev) => [...prev, newCollection]);
        return { success: true, collection: { id: newCollection.id, name: newCollection.name } };
      }
      if (isAuthenticated && !currentWorkspace) {
        return { error: 'No workspace selected' };
      }
      const capped = await apiCapRefusal('collections');
      if (capped) return { error: capped };
      const newCollection = markLocalOnly({ id: Date.now().toString(), name: trimmed, requests: [] });
      setCollections((prev) => [...prev, newCollection]);
      return { success: true, collection: { id: newCollection.id, name: newCollection.name } };
    } catch (error) {
      if (error.response?.data?.upgrade_required) {
        return { error: error.response.data.error || 'Upgrade required' };
      }
      return { error: error.message || 'Failed to create collection' };
    }
  }, []);

  // Batch (22.08.2026) — replaces the single-folder aiCreateFolder. The tree
  // logic is the pure applyFolderBatch; this wrapper is the one store read,
  // the one setCollections and the one scheduleTreeSave per call. Folders are
  // collection settings for local and cloud collections alike, so there is no
  // per-folder API call and no local/cloud branch here.
  const aiCreateFolders = useCallback(async (input = {}) => {
    const collectionId = input.collection_id;
    if (!collectionId) return { error: 'collection_id is required' };

    const snapshot = useCollectionsStore.getState().collections;
    const collection = snapshot.find((c) => String(c.id) === String(collectionId));
    if (!collection) return { error: 'Collection not found' };
    // Same predicate the Sidebar's "New folder" item uses (`can_edit !== false`,
    // Sidebar.js). `can_edit` is server-stamped and receiver-relative; without
    // this check up to 100 phantom folders land locally, the model is told
    // success, and the tree save's refusal wipes them 500 ms later (NB1).
    if (collection.can_edit === false) {
      return { error: `You cannot edit the collection "${collection.name || collectionId}" — it is shared read-only` };
    }

    const batch = applyFolderBatch(collection, input.folders);
    if (batch.error) return { error: batch.error };

    if (batch.created_count > 0) {
      const nextCollections = snapshot.map((c) => (String(c.id) === String(collectionId) ? batch.collection : c));
      setCollections(nextCollections);
      scheduleTreeSave(nextCollections);
    }

    return {
      success: batch.failed_count === 0,
      collection_id: String(collectionId),
      collection_name: collection.name,
      created_count: batch.created_count,
      existing_count: batch.existing_count,
      failed_count: batch.failed_count,
      folders: batch.folders,
    };
  }, [scheduleTreeSave]);

  const aiCreateRequest = useCallback(async (input) => {
    const collectionId = input.collection_id;
    const folderId = input.folder_id ?? null;
    const collection = useCollectionsStore.getState().collections.find((c) => String(c.id) === String(collectionId));
    if (!collection) return { error: 'Collection not found' };
    if (folderId && !getFolderById(collection, folderId)) {
      return { error: 'Folder not found' };
    }
    const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
    const isAuthenticated = useAuthStore.getState().isAuthenticated;
    const readOnly = readOnlyWorkspaceRefusal(currentWorkspace);
    if (readOnly) return { error: readOnly };

    const headerRows = input.headers && typeof input.headers === 'object'
      ? Object.entries(input.headers)
        .filter(([key]) => key)
        .map(([key, value]) => ({
          key: normalizeSingleBraceVariables(key),
          value: normalizeSingleBraceVariables(String(value ?? '')),
          enabled: true,
        }))
      : [];
    const requestData = {
      name: resolvedRequestName(input.name, input.url),
      method: input.method || 'GET',
      url: normalizeSingleBraceVariables(input.url || ''),
      headers: headerRows.length > 0 ? headerRows : [{ key: '', value: '', enabled: true }],
      params: [{ key: '', value: '', enabled: true }],
      cookies: [{ key: '', value: '', enabled: true }],
      body: input.body || '',
      bodyType: input.body ? 'json' : 'none',
      auth: defaultRequestAuth(),
      folder_id: folderId,
    };

    try {
      if (isBackendWorkspace(currentWorkspace, isAuthenticated)) {
        const newRequest = await apiRequestService.createRequest(
          prepareRequestBodyForApi(requestData, collectionId, requestData.name)
        );
        const mappedNew = mapApiRequestToSidebar(newRequest);
        const mappedWithFolder = folderId
          ? { ...mappedNew, folder_id: folderId, settings: { ...(mappedNew.settings || {}), folder_id: folderId } }
          : mappedNew;
        setCollections((prev) => prev.map((c) =>
          String(c.id) === String(collectionId)
            ? { ...c, requests: [...(c.requests || []), mappedWithFolder] }
            : c
        ));
        return {
          success: true,
          request: {
            id: mappedWithFolder.id, name: mappedWithFolder.name, method: mappedWithFolder.method,
            url: mappedWithFolder.url, collection_id: collectionId, folder_id: folderId,
          },
        };
      }

      const capped = await apiCapRefusal('requests');
      if (capped) return { error: capped };
      const newRequest = { id: generateLocalEntityId('req'), ...requestData, folder_id: folderId };
      setCollections((prev) => dedupeCollectionRequestIds(prev.map((c) =>
        String(c.id) === String(collectionId)
          ? { ...c, requests: [...(c.requests || []), newRequest] }
          : c
      )));
      return {
        success: true,
        request: {
          id: newRequest.id, name: newRequest.name, method: newRequest.method,
          url: newRequest.url, collection_id: collectionId, folder_id: folderId,
        },
      };
    } catch (error) {
      return { error: error.message || 'Failed to create request' };
    }
  }, []);

  const aiMoveRequests = useCallback(async (input) => {
    const collectionId = input.collection_id;
    const moves = Array.isArray(input.moves) ? input.moves : [];
    if (!collectionId || moves.length === 0) {
      return { error: 'collection_id and moves array are required' };
    }

    const snapshot = useCollectionsStore.getState().collections;
    const collection = snapshot.find((c) => String(c.id) === String(collectionId));
    if (!collection) return { error: 'Collection not found' };

    let next = collection;
    const moved = [];
    for (const move of moves) {
      const requestId = move.request_id;
      const folderId = move.folder_id ?? null;
      if (folderId && !getFolderById(next, folderId)) {
        moved.push({ request_id: requestId, error: 'Folder not found' });
        continue;
      }
      const req = (next.requests || []).find((r) => String(r.id) === String(requestId));
      if (!req) {
        moved.push({ request_id: requestId, error: 'Request not found' });
        continue;
      }
      next = moveRequestToFolder(next, requestId, folderId);
      const folderName = folderId ? getFolderById(next, folderId)?.name : null;
      moved.push({ success: true, request_id: String(requestId), request_name: req.name, folder_id: folderId, folder_name: folderName });
    }

    const nextCollections = snapshot.map((c) => (String(c.id) === String(collectionId) ? next : c));
    setCollections(nextCollections);
    scheduleTreeSave(nextCollections);

    const ok = moved.filter((m) => m.success).length;
    const failed = moved.filter((m) => m.error).length;
    return { success: failed === 0, moved_count: ok, failed_count: failed, moved };
  }, [scheduleTreeSave]);

  // P2 — narrow patch to a saved request (name/method/url/headers/body). Routed
  // through the AI Confirm card. Persists to backend or the local store and syncs
  // any open tab. Intentionally NOT a mega "propose fix" tool.
  const aiUpdateRequest = useCallback(async (input = {}) => {
    const requestId = input.request_id;
    if (!requestId) return { error: 'request_id is required' };

    const snapshot = useCollectionsStore.getState().collections;
    const existing = findSidebarRequest(snapshot, requestId);
    if (!existing) return { error: 'Request not found' };
    const collectionId = findParentCollectionId(snapshot, requestId);
    if (!collectionId) return { error: 'Collection not found' };

    const patch = {};
    if (typeof input.name === 'string') patch.name = input.name;
    if (typeof input.method === 'string') patch.method = input.method.toUpperCase();
    if (typeof input.url === 'string') patch.url = normalizeSingleBraceVariables(input.url);
    if (typeof input.body === 'string') {
      patch.body = input.body;
      patch.bodyType = input.body ? (existing.bodyType && existing.bodyType !== 'none' ? existing.bodyType : 'json') : 'none';
    }
    // The mock example list travels the same road as every other field: both
    // save paths already carry `mock` (requestPayload.js:68 for the cloud PUT,
    // useCollections.js for the local tree), so a tool that edits mocks reuses
    // all of that instead of writing its own persistence.
    if (input.mock && typeof input.mock === 'object' && !Array.isArray(input.mock)) {
      patch.mock = input.mock;
    }
    if (input.headers && typeof input.headers === 'object') {
      patch.headers = Object.entries(input.headers)
        .filter(([key]) => key)
        .map(([key, value]) => ({
          key: normalizeSingleBraceVariables(key),
          value: normalizeSingleBraceVariables(String(value ?? '')),
          enabled: true,
        }));
      if (patch.headers.length === 0) patch.headers = [{ key: '', value: '', enabled: true }];
    }
    if (Object.keys(patch).length === 0) return { error: 'No supported fields to update' };

    const merged = { ...existing, ...patch };
    const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
    const isAuthenticated = useAuthStore.getState().isAuthenticated;

    try {
      if (isBackendWorkspace(currentWorkspace, isAuthenticated)) {
        const saved = await apiRequestService.updateRequest(
          requestId,
          prepareRequestBodyForApi(merged, collectionId, merged.name)
        );
        const mappedSaved = mapApiRequestToSidebar(saved);
        setCollections((prev) => prev.map((c) =>
          String(c.id) === String(collectionId)
            ? { ...c, requests: (c.requests || []).map((r) => (String(r.id) === String(requestId) ? { ...mappedSaved, folder_id: r.folder_id } : r)) }
            : c
        ));
      } else {
        const nextCollections = snapshot.map((c) =>
          String(c.id) === String(collectionId)
            ? { ...c, requests: (c.requests || []).map((r) => (String(r.id) === String(requestId) ? merged : r)) }
            : c
        );
        setCollections(nextCollections);
        scheduleTreeSave(nextCollections);
      }

      // Sync an open tab for this request so the editor reflects the change.
      setRequestTabs((prev) => prev.map((t) =>
        String(t.originalId) === String(requestId) ? { ...t, ...patch, hasUnsavedChanges: false } : t
      ));
      setRequestTabSyncRevision((n) => n + 1);

      const changed = Object.keys(patch);
      return {
        success: true,
        request_id: String(requestId),
        updated_fields: changed,
        // UI-side only. condenseToolResultForApi projects `update_request`
        // down to { success, request_id, updated_fields }, so these values
        // never cross to the model — they exist so the chat card can show a
        // real before → after diff instead of a bare list of field names.
        changes: changed.map((field) => ({
          field,
          before: describeRequestFieldValue(field, existing[field]),
          after: describeRequestFieldValue(field, patch[field]),
        })),
        request: { id: requestId, name: merged.name, method: merged.method, url: merged.url },
      };
    } catch (error) {
      return { error: error.message || 'Failed to update request' };
    }
  }, [scheduleTreeSave]);

  return { aiCreateCollection, aiCreateFolders, aiCreateRequest, aiMoveRequests, aiUpdateRequest };
}
